<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  defaultApplicationName: {
    type: String,
    required: true,
  },

  id: {
    type: Number,
    default: 1,
  },

  prefilledApplication: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['show', 'application-selected', 'edit-application', 'remove-application']);

const showApplicationToolbar = ref(false);

const applicationLogo = computed(() => {
  const app = props.prefilledApplication;
  let applicationInfo = app;

  if (app.placeholderApplication != null) {
    applicationInfo = app.placeholderApplication;
    applicationInfo.name = app.placeholderApplication.display_name;
  } else if (app.application != null) {
    applicationInfo = app.application;
  }
  const logoFallback = `https://avatars.dicebear.com/api/identicon/${applicationInfo.name}.svg`;
  const logo = applicationInfo.logo_url;
  return logo ? logo : logoFallback;
});

const show = () => {
  if (props.prefilledApplication === null) {
    emit('show', props.id);
  }
};

const toggleApplicationToolbar = () => (showApplicationToolbar.value = !showApplicationToolbar.value);

const editApplication = () => {
  toggleApplicationToolbar();
  emit('edit-application', props.prefilledApplication);
};

const deleteApplicationFromWizard = () => {
  toggleApplicationToolbar();

  emit('remove-application', props.id);
};
</script>

<template>
  <div class="">
    <div class="tile p-6 cursor-pointer text-center" @click="show">
      <div v-if="prefilledApplication == null">
        <svg class="icon_add" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
          />
        </svg>
      </div>
      <div v-else>
        <svg class="application_toolbar_toggle" @click="toggleApplicationToolbar">
          <use xlink:href="#dots-vertical"></use>
        </svg>
        <div
          class="application_toolbar"
          :class="{ active: showApplicationToolbar }"
          @mouseleave="toggleApplicationToolbar"
        >
          <p class="application_tool" @click.stop="editApplication()">
            <svg class="icon">
              <use xlink:href="#edit-icon"></use>
            </svg>
            {{ trans('common.edit') }}
          </p>
          <p class="application_tool" @click.stop="deleteApplicationFromWizard">
            <svg class="icon">
              <use xlink:href="#delete-icon"></use>
            </svg>
            {{ trans('common.delete') }}
          </p>
        </div>
        <div class="px-6 pt-4 pb-2 gap-4 my-1 text-center">
          <img :src="applicationLogo" height="100" width="100" alt="application-logo" />
        </div>
      </div>
      <div class="mt-2">
        <div v-if="prefilledApplication">
          {{ prefilledApplication.alias || prefilledApplication.application?.name }}
        </div>
        <div v-else>{{ defaultApplicationName }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.application {
  &_tool {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-start;

    & + & {
      padding-top: 0.75rem;
    }

    svg {
      --length: 1rem;
      height: var(--length);
      width: var(--length);
    }

    &bar {
      width: 100%;
      max-width: 11.5rem;
      position: absolute;
      z-index: 1;
      right: 1rem;
      top: 2.5rem;
      background: var(--light);
      padding: 1rem;
      border: 2px solid var(--table-row-color);
      border-radius: 0.5rem;

      &:not(.active) {
        display: none;
      }

      &_toggle {
        height: 1.25rem;
        width: 1.25rem;
        position: absolute;
        right: 0.5rem;
        top: 0.5rem;
      }
    }
  }
}
</style>
